import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SEO from '../../components/SEO';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterSidebar from './components/FilterSidebar';
import StoreCard from './components/StoreCard';
import StoreList from './components/StoreList';
import ViewToggle from './components/ViewToggle';
import SortDropdown from './components/SortDropdown';
import { obterTodasEmpresas, buscarEmpresasPorNome, filtrarEmpresasPorTipo } from '../../data/empresas';
import { generatePageSEO } from '../../utils/seoData';
import { useSearchFilters } from '../../hooks/useSearchFilters';

const StoreDirectory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Hook do Redux para filtros
  const { 
    searchQuery, 
    selectedCategory, 
    categories,
    priceRanges,
    services,
    hasPromotions,
    isOpen,
    appliedFilters, 
    isFilterApplied,
    applyCategoryFilter,
    applySearchFilter,
    setCategories,
    setPriceRanges,
    setServices,
    setHasPromotions,
    setIsOpen,
    updateFilters,
    clearFilters
  } = useSearchFilters();

  const seoData = generatePageSEO('storeDirectory');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Obter dados das empresas
  const empresas = obterTodasEmpresas();



  // Sincronizar filtros do Redux com URL params (opcional para SEO)
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    
    if (selectedCategory) {
      params.set('category', selectedCategory);
    }
    
    // Atualizar URL apenas se há mudanças
    const newParams = params.toString();
    const currentParams = searchParams.toString();
    
    if (newParams !== currentParams) {
      setSearchParams(newParams ? `?${newParams}` : '');
    }
  }, [searchQuery, selectedCategory, searchParams, setSearchParams]);

  // Filter and sort stores
  const filteredAndSortedStores = useMemo(() => {
    let filtered = empresas?.filter(empresa => {
      // Search term filter (do Redux)
      if (searchQuery) {
        const searchLower = searchQuery?.toLowerCase();
        if (!empresa?.nome?.toLowerCase()?.includes(searchLower) &&
            !empresa?.tipo?.toLowerCase()?.includes(searchLower) &&
            !empresa?.descricao?.toLowerCase()?.includes(searchLower) &&
            !empresa?.servicos?.some(servico => servico?.toLowerCase()?.includes(searchLower))) {
          return false;
        }
      }

      // Category filter (do Redux) - agora usa os tipos reais das lojas
      if (categories?.length > 0) {
        // Converter IDs de categoria de volta para tipos reais
        // IDs são criados como: tipo.toLowerCase().replace(/\s+/g, '-').replace(/[\/]/g, '-')
        const hasMatchingCategory = categories?.some(categoryId => {
          // Normalizar o tipo da empresa da mesma forma que o ID foi criado
          const tipoNormalizado = empresa?.tipo?.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[\/]/g, '-')
            .replace(/&/g, 'e');
          
          return categoryId === tipoNormalizado;
        });
        
        if (!hasMatchingCategory) {
          return false;
        }
      }

      // Price range filter
      if (priceRanges?.length > 0) {
        const empresaPreco = empresa?.preco;
        const hasMatchingPrice = priceRanges?.some(range => {
          switch (range) {
            case '0-50':
              return empresaPreco === '0-50' || (typeof empresaPreco === 'string' && empresaPreco.includes('R$ 0 - R$ 50'));
            case '51-150':
              return empresaPreco === '51-150' || (typeof empresaPreco === 'string' && empresaPreco.includes('R$ 51 - R$ 150'));
            case '151-300':
              return empresaPreco === '151-300' || (typeof empresaPreco === 'string' && empresaPreco.includes('R$ 151 - R$ 300'));
            case '300+':
              return empresaPreco === '300+' || (typeof empresaPreco === 'string' && empresaPreco.includes('R$ 300+'));
            default:
              return false;
          }
        });
        
        if (!hasMatchingPrice) {
          return false;
        }
      }

      // Services filter
      if (services?.length > 0) {
        const hasRequiredServices = services?.every(service => 
          empresa?.servicos && empresa?.servicos?.some(servico => 
            servico?.toLowerCase()?.includes(service?.toLowerCase())
          )
        );
        if (!hasRequiredServices) {
          return false;
        }
      }

      // Promotions filter
      if (hasPromotions && !empresa?.promocao) {
        return false;
      }

      // Open now filter
      if (isOpen && !empresa?.aberto) {
        return false;
      }

      return true;
    });

    // Sort stores
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.nome?.localeCompare(b?.nome);
        case 'rating':
          return b?.avaliacao - a?.avaliacao;
        case 'category':
          return a?.tipo?.localeCompare(b?.tipo);
        case 'popular':
          return b?.numeroAvaliacoes - a?.numeroAvaliacoes;
        case 'newest':
          return b?.id?.localeCompare(a?.id);
        default:
          return 0;
      }
    });

    return filtered;
  }, [empresas, searchQuery, categories, priceRanges, services, hasPromotions, isOpen, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedStores?.length / itemsPerPage);
  const paginatedStores = filteredAndSortedStores?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'categories':
        setCategories(value);
        break;
      case 'priceRanges':
        setPriceRanges(value);
        break;
      case 'services':
        setServices(value);
        break;
      case 'hasPromotions':
        setHasPromotions(value);
        break;
      case 'isOpen':
        setIsOpen(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    clearFilters();
    setCurrentPage(1);
    setSearchParams({});
  };

  const handleSearchChange = (term) => {
    // Aplicar filtro de busca no Redux
    if (term) {
      applySearchFilter(term);
    } else {
      clearFilters();
    }
    setCurrentPage(1);
    
    if (term) {
      setSearchParams({ search: term });
    } else {
      setSearchParams({});
    }
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO {...seoData} />
      <Header />
      <div className="flex pt-16">
        {/* Filter Sidebar */}
        <FilterSidebar
          categories={categories}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          searchTerm={searchQuery}
          onSearchChange={handleSearchChange}
          isFilterOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        {/* Main Content */}
        <div className="flex-1 lg:ml-0">
          <div className="p-6">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                    Lojas
                  </h1>
                  <p className="text-muted-foreground">
                    Estação: o seu ponto de encontro no Altiplano Leste.
                  </p>
                </div>

                {/* Mobile Filter Button */}
                <Button
                  variant="outline"
                  onClick={() => setIsFilterOpen(true)}
                  className="lg:hidden"
                >
                  <Icon name="Filter" size={16} className="mr-2" />
                  Filtros
                </Button>
              </div>

              {/* Results Info and Controls */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {filteredAndSortedStores?.length} {filteredAndSortedStores?.length === 1 ? 'loja encontrada' : 'lojas encontradas'}
                  </span>
                  
                  {(searchQuery || categories?.length > 0 || priceRanges?.length > 0 || 
                    services?.length > 0 || hasPromotions || isOpen) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      className="text-xs"
                    >
                      <Icon name="X" size={14} className="mr-1" />
                      Limpar filtros
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <SortDropdown
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                  />
                  
                  <ViewToggle
                    view={view}
                    onViewChange={handleViewChange}
                  />
                </div>
              </div>
            </div>

            {/* Active Filters */}
            {(categories?.length > 0 || priceRanges?.length > 0 || 
              services?.length > 0 || hasPromotions || isOpen) && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {categories?.map((categoryId) => {
                    const categoryLabels = {
                      'alimentacao': 'Alimentação',
                      'moda': 'Moda & Vestuário',
                      'beleza': 'Beleza & Estética',
                      'servicos': 'Serviços',
                      'saude': 'Saúde & Bem-estar',
                      'tecnologia': 'Tecnologia',
                      'casa': 'Casa & Decoração',
                      'educacao': 'Educação'
                    };
                    return (
                      <span
                        key={categoryId}
                        className="inline-flex items-center px-3 py-1 bg-primary text-primary-foreground rounded-full text-sm"
                      >
                        {categoryLabels[categoryId] || categoryId}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newCategories = categories.filter(cat => cat !== categoryId);
                            setCategories(newCategories);
                          }}
                          className="ml-2 hover:bg-primary-foreground/20 rounded-full p-0.5 transition-colors"
                          aria-label={`Remover filtro ${categoryLabels[categoryId] || categoryId}`}
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    );
                  })}
                  
                  {hasPromotions && (
                    <span className="inline-flex items-center px-3 py-1 bg-accent text-accent-foreground rounded-full text-sm">
                      Com promoções
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setHasPromotions(false);
                        }}
                        className="ml-2 hover:bg-accent-foreground/20 rounded-full p-0.5 transition-colors"
                        aria-label="Remover filtro Com promoções"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  )}
                  
                  {isOpen && (
                    <span className="inline-flex items-center px-3 py-1 bg-success text-success-foreground rounded-full text-sm">
                      Aberto agora
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsOpen(false);
                        }}
                        className="ml-2 hover:bg-success-foreground/20 rounded-full p-0.5 transition-colors"
                        aria-label="Remover filtro Aberto agora"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  )}
                  
                  {priceRanges?.length > 0 && priceRanges.map((range) => (
                    <span key={range} className="inline-flex items-center px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm">
                      {range === '0-50' ? 'R$ 0 - R$ 50' : 
                       range === '51-150' ? 'R$ 51 - R$ 150' :
                       range === '151-300' ? 'R$ 151 - R$ 300' :
                       range === '300+' ? 'R$ 300+' : range}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newRanges = priceRanges.filter(r => r !== range);
                          setPriceRanges(newRanges);
                        }}
                        className="ml-2 hover:bg-secondary-foreground/20 rounded-full p-0.5 transition-colors"
                        aria-label={`Remover filtro ${range}`}
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  ))}
                  
                  {services?.length > 0 && services.map((service) => (
                    <span key={service} className="inline-flex items-center px-3 py-1 bg-muted text-foreground rounded-full text-sm border border-border">
                      {service}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newServices = services.filter(s => s !== service);
                          setServices(newServices);
                        }}
                        className="ml-2 hover:bg-muted-foreground/20 rounded-full p-0.5 transition-colors"
                        aria-label={`Remover filtro ${service}`}
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}


            {/* Store Grid/List */}
            {paginatedStores?.length > 0 ? (
              <>
                {view === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                    {paginatedStores?.map(store => (
                      <StoreCard
                        key={store?.id}
                        store={store}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="mb-8">
                    <StoreList
                      stores={paginatedStores}
                    />
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <Icon name="ChevronLeft" size={16} />
                      Anterior
                    </Button>

                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)]?.map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "default" : "ghost"}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className="w-10 h-10"
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span key={page} className="px-2 text-muted-foreground">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                      <Icon name="ChevronRight" size={16} />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              /* No Results */
              (<div className="text-center py-12">
                <div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  Nenhuma loja encontrada
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Não encontramos lojas que correspondam aos seus critérios de busca. 
                  Tente ajustar os filtros ou buscar por outros termos.
                </p>
                <Button onClick={handleClearFilters}>
                  <Icon name="RotateCcw" size={16} className="mr-2" />
                  Limpar filtros
                </Button>
              </div>)
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreDirectory;